import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { useRemoveMemberBulkDialogContent } from "@components/table/members/bulk-actions/bulk-remove-member-helper";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { removeMemberFromGroup } from "@store/groups/groups-slice-thunk";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedProjectManagerSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { removeMembersByEmails } from "@store/teams/teams-slice";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect } from "react";

/** Props for the bulk remove member dialog */

/**
 * Dialog for removing members in bulk from various contexts (workspace, team, project, group).
 * Handles the confirmation dialog and dispatches the appropriate removal actions.
 */
export function BulkRemoveMemberDialog(t0) {
  const $ = _c(27);
  const {
    companyId,
    projectId,
    groupId,
    teamId,
    subjectType,
    onGroupRemoved,
    isOpen,
    onClose
  } = t0;
  const {
    user: selectedProjectManager
  } = useAppSelector(selectedProjectManagerSelector);
  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("members");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selectedEntitiesSelector("teams");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectedTeams = useAppSelector(t2);
  const {
    bulkDialogContent
  } = useRemoveMemberBulkDialogContent(subjectType);
  const refreshSeatUsage = useRefreshSeatUsage();
  const isRemovingFromWorkspace = subjectType === "workspace" || subjectType === "workspaceTeam";
  const selectedEntitiesWithoutTeams = selectedEntities.filter(_temp);
  let t3;
  let t4;
  if ($[2] !== isOpen || $[3] !== onClose || $[4] !== shouldShowSuccessDialog) {
    t3 = () => {
      if (shouldShowSuccessDialog && isOpen) {
        onClose();
      }
    };
    t4 = [shouldShowSuccessDialog, isOpen, onClose];
    $[2] = isOpen;
    $[3] = onClose;
    $[4] = shouldShowSuccessDialog;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== dispatch || $[8] !== isOpen || $[9] !== selectedProjectManager || $[10] !== subjectType) {
    t5 = () => {
      if (isOpen) {
        dispatch(setBulkActionName("removeMember"));
        if (subjectType === "project" && selectedProjectManager) {
          dispatch(updateOne({
            id: selectedProjectManager.identity,
            changes: {
              status: "not-allowed",
              message: "Cannot remove owner of project"
            }
          }));
        }
      }
    };
    t6 = [isOpen, subjectType, selectedProjectManager, dispatch];
    $[7] = dispatch;
    $[8] = isOpen;
    $[9] = selectedProjectManager;
    $[10] = subjectType;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== companyId || $[14] !== dispatch || $[15] !== groupId || $[16] !== onGroupRemoved || $[17] !== projectId || $[18] !== subjectType || $[19] !== teamId) {
    t7 = function onRemoveMemberConfirmed(member) {
      switch (subjectType) {
        case "workspace":
          {
            return dispatch(removeMemberFromCompany({
              companyId,
              member
            })).unwrap().then(response => dispatch(removeMembersByEmails([response.email])));
          }
        case "workspaceTeam":
          {
            return dispatch(removeMemberFromCompany({
              companyId,
              member
            })).unwrap();
          }
        case "project":
          {
            return dispatch(removeMemberFromProject({
              companyId,
              member,
              projectId
            })).unwrap();
          }
        case "group":
          {
            return dispatch(removeMemberFromGroup({
              companyId,
              member,
              groupId,
              onGroupRemoved
            })).unwrap();
          }
        case "team":
          {
            return dispatch(removeMembersFromTeam({
              companyId,
              teamId,
              memberIds: [member.identity]
            })).unwrap();
          }
      }
    };
    $[13] = companyId;
    $[14] = dispatch;
    $[15] = groupId;
    $[16] = onGroupRemoved;
    $[17] = projectId;
    $[18] = subjectType;
    $[19] = teamId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const onRemoveMemberConfirmed = t7;
  let closeDialog;
  let onSuccessfulComplete;
  if ($[21] !== dispatch || $[22] !== isRemovingFromWorkspace || $[23] !== onClose || $[24] !== refreshSeatUsage) {
    onSuccessfulComplete = function onSuccessfulComplete() {
      refreshSeatUsageIfRemovedFromWorkspace();
      dispatch(setBulkActionName(null));
      onClose();
    };
    closeDialog = function closeDialog() {
      refreshSeatUsageIfRemovedFromWorkspace();
      onClose();
    };
    function refreshSeatUsageIfRemovedFromWorkspace() {
      if (isRemovingFromWorkspace) {
        refreshSeatUsage();
      }
    }
    $[21] = dispatch;
    $[22] = isRemovingFromWorkspace;
    $[23] = onClose;
    $[24] = refreshSeatUsage;
    $[25] = closeDialog;
    $[26] = onSuccessfulComplete;
  } else {
    closeDialog = $[25];
    onSuccessfulComplete = $[26];
  }
  if (!isOpen) {
    return null;
  }
  return <FaroBulkDialog dialogContent={bulkDialogContent} onClose={closeDialog} trackingEvent={BulkActionEvents.bulkRemoveMember} bulkActionCallback={onRemoveMemberConfirmed} onSuccessfulComplete={onSuccessfulComplete} selectedEntities={[...selectedEntitiesWithoutTeams, ...selectedTeams]} uniqueIdKey="identity" tableDataType="members" />;
}
function _temp(entities) {
  return entities.kind === "user";
}